import { useAppRouteParams } from "@/AppRoutes";
import {
  getAdminFrontendAddonVersions,
  postAdminSetFrontendAddonVersion,
} from "@/api/admin/frontendAddons";
import {
  getFrontendAddonVersions,
  postSetFrontendAddonVersion,
} from "@/api/frontendAddons";
import { useAppAccount } from "@/components/AppProvider/AppAccountProvider";
import { useAppNotificationManager } from "@/components/AppProvider/AppNotificationProvider";
import { usePermissions } from "@/components/InOrganizationProvider/PermissionsProvider";
import { InfoTooltipLabel } from "@/components/InfoTooltipLabel";
import { LayoutTemplate } from "@/components/LayoutTemplate";
import { LoadingCover } from "@/components/LoadingCover";
import {
  FrontendAddonVersionRaw,
  FrontendAddonWithTargetVersionRaw,
} from "@/domain/FrontendAddon";
import {
  Button,
  Dropdown,
  Option,
  Radio,
  RadioGroup,
} from "@fluentui/react-components";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { DialogPhaseType, useDialog } from "../../../../Dialog/helpers/hooks";
import { WarningMessageForProduction } from "../../../helpers/WarningMessageForProduction";
import cardTitleStyles from "../../../helpers/cardTitle.module.css";
import {
  formatFrontendAddonVersionDisplayName,
  getFrontendAddonVersionName,
} from "../../../helpers/frontendAddonVersionDisplay";
import { DialogFrontendAddonVersionBulkAction } from "./components/DialogFrontendAddonVersionBulkAction";
import { DialogFrontendAddonVersionSingleAction } from "./components/DialogFrontendAddonVersionSingleAction";
import { GridTableFrontendAddonVersions } from "./components/GridTableFrontendAddonVersions";
import styles from "./index.module.css";
import { FrontendAddonUsageOrg, FrontendAddonVersionAction } from "./types";

type TargetVersionMode = "latest" | "fixed";

export const FrontendAddonVersionManagement: React.FC<{
  isProd: boolean;
  targetFrontendAddon: FrontendAddonWithTargetVersionRaw | undefined;
  runReloadFrontendAddons?: () => Promise<void>;
  versionUpdatePath: string;
  showVersionSettings?: boolean;
}> = ({
  isProd,
  targetFrontendAddon,
  runReloadFrontendAddons,
  versionUpdatePath,
  showVersionSettings = true,
}) => {
  const { organizationId, frontendAddonId } = useAppRouteParams();
  const navigate = useNavigate();
  const { isAADeveloperOrAbove } = useAppAccount();
  const { hasPermission } = usePermissions();
  const { showErrorNotification, showSuccessNotification } =
    useAppNotificationManager();

  const hasWritePermission =
    isAADeveloperOrAbove || hasPermission("frontend_addons_owned", "write");
  const hasDeletePermission =
    isAADeveloperOrAbove || hasPermission("frontend_addons_owned", "delete");

  const [isLoading, setIsLoading] = useState(true);
  const [versions, setVersions] = useState<FrontendAddonVersionRaw[]>([]);
  const [latestUsageOrgs, setLatestUsageOrgs] = useState<
    FrontendAddonUsageOrg[]
  >([]);

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null
  );
  const [selectedAction, setSelectedAction] =
    useState<FrontendAddonVersionAction | null>(null);
  const [dialogPhase, setDialogPhase] = useState<DialogPhaseType>(null);

  const [selectedVersionIds, setSelectedVersionIds] = useState<string[]>([]);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);

  const fetchFrontendAddonVersions = useCallback(async () => {
    setIsLoading(true);
    try {
      if (isAADeveloperOrAbove) {
        const { items, latest_usage_orgs } =
          await getAdminFrontendAddonVersions({ frontendAddonId });
        setVersions(items);
        setLatestUsageOrgs(latest_usage_orgs ?? []);
      } else {
        setVersions(
          await getFrontendAddonVersions({ organizationId, frontendAddonId })
        );
      }
    } catch (error) {
      showErrorNotification("バージョン一覧の取得に失敗しました", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    frontendAddonId,
    isAADeveloperOrAbove,
    organizationId,
    showErrorNotification,
  ]);

  useEffect(() => {
    fetchFrontendAddonVersions();
  }, [fetchFrontendAddonVersions]);

  const initialMode: TargetVersionMode =
    targetFrontendAddon?.target_version === "latest" ? "latest" : "fixed";
  const initialFixedVersionId =
    targetFrontendAddon && targetFrontendAddon.target_version !== "latest"
      ? targetFrontendAddon.target_version
      : "";

  const [targetVersionMode, setTargetVersionMode] =
    useState<TargetVersionMode>(initialMode);
  const [fixedVersionId, setFixedVersionId] = useState(initialFixedVersionId);

  // バージョン削除後のアドオン再取得で保存前の使用設定が巻き戻らないよう、サーバー側の値が変わったときだけ同期する
  const serverTargetVersion = targetFrontendAddon?.target_version;
  useEffect(() => {
    if (serverTargetVersion === undefined) return;
    setTargetVersionMode(serverTargetVersion === "latest" ? "latest" : "fixed");
    setFixedVersionId(
      serverTargetVersion === "latest" ? "" : serverTargetVersion
    );
  }, [serverTargetVersion]);

  const targetVersion =
    targetVersionMode === "latest" ? "latest" : fixedVersionId;
  const isTargetVersionChanged =
    targetFrontendAddon?.target_version !== targetVersion;

  const sortedVersions = useMemo(
    () =>
      [...versions]
        .filter((v) => !v.is_disabled)
        .sort((a, b) => b.updated_at.localeCompare(a.updated_at)),
    [versions]
  );

  const selectedFixedVersionName = useMemo(() => {
    const version = versions.find((v) => v.id === fixedVersionId);
    if (version) return formatFrontendAddonVersionDisplayName(version);
    if (!fixedVersionId || !targetFrontendAddon) return "";
    return formatFrontendAddonVersionDisplayName({
      id: fixedVersionId,
      name: targetFrontendAddon.target_version_name,
      updated_at: targetFrontendAddon.target_version_updated_at,
    });
  }, [versions, fixedVersionId, targetFrontendAddon]);

  const latestVersionName = targetFrontendAddon
    ? getFrontendAddonVersionName({
        id: targetFrontendAddon.latest_version,
        name: targetFrontendAddon.latest_version_name,
      })
    : "";

  const [targetVersionDialogPhase, setTargetVersionDialogPhase] =
    useState<DialogPhaseType>(null);
  const { renderDialogs: renderTargetVersionDialogs, isInProgress } = useDialog(
    {
      dialogPhase: targetVersionDialogPhase,
      setDialogPhase: setTargetVersionDialogPhase,
    }
  );

  const runSaveTargetVersion = useCallback(async () => {
    setTargetVersionDialogPhase("in_progress");
    try {
      isAADeveloperOrAbove
        ? await postAdminSetFrontendAddonVersion({
            organizationId,
            frontendAddonId,
            targetVersion: { target_version: targetVersion },
          })
        : await postSetFrontendAddonVersion({
            organizationId,
            frontendAddonId,
            targetVersion: { target_version: targetVersion },
          });
      await runReloadFrontendAddons?.();
      // 使用設定の変更は各バージョンの利用組織 (直接指定) に反映されるため一覧も再取得する
      await fetchFrontendAddonVersions();
      showSuccessNotification("バージョン使用設定を更新しました");
    } catch (error) {
      showErrorNotification("バージョン使用設定の更新に失敗しました", error);
    }
    setTargetVersionDialogPhase(null);
  }, [
    isAADeveloperOrAbove,
    organizationId,
    frontendAddonId,
    targetVersion,
    runReloadFrontendAddons,
    fetchFrontendAddonVersions,
    showSuccessNotification,
    showErrorNotification,
  ]);

  const runCancelTargetVersion = useCallback(() => {
    if (!targetFrontendAddon) return;
    setTargetVersionMode(
      targetFrontendAddon.target_version === "latest" ? "latest" : "fixed"
    );
    setFixedVersionId(
      targetFrontendAddon.target_version === "latest"
        ? ""
        : targetFrontendAddon.target_version
    );
  }, [targetFrontendAddon]);

  const isDisableTargetVersion =
    !hasWritePermission ||
    isInProgress ||
    !targetFrontendAddon ||
    targetFrontendAddon.is_disabled ||
    !targetFrontendAddon.is_editable;

  const selectedBulkVersions = useMemo(
    () => versions.filter((version) => selectedVersionIds.includes(version.id)),
    [versions, selectedVersionIds]
  );

  const renderTargetVersionRadioCard = ({
    mode,
    title,
    description,
    children,
  }: {
    mode: TargetVersionMode;
    title: string;
    description: string;
    children?: React.ReactNode;
  }) => {
    const isSelected = targetVersionMode === mode;
    return (
      <div
        className={`${styles.targetVersionRadioCard} ${
          isSelected ? styles.targetVersionRadioCardSelected : ""
        } ${isDisableTargetVersion ? styles.targetVersionRadioCardDisabled : ""}`}
        onClick={() => {
          if (isDisableTargetVersion) return;
          setTargetVersionMode(mode);
        }}
      >
        <Radio
          value={mode}
          label={title}
          checked={isSelected}
          disabled={isDisableTargetVersion}
          onChange={() => setTargetVersionMode(mode)}
        />
        <div className={styles.targetVersionRadioBody}>
          <div className={styles.targetVersionRadioDescription}>
            {description}
          </div>
          {children}
        </div>
      </div>
    );
  };

  if (isLoading) return <LoadingCover />;

  return (
    <>
      <div className={styles.cardsContainer}>
        {showVersionSettings ? (
          <div className={styles.targetVersionCard}>
            <LayoutTemplate
              leftHeaderElement={
                <>
                  “
                  <span
                    className={`${cardTitleStyles.cardTitle} ${styles.cardTitle}`}
                    title={targetFrontendAddon?.name ?? ""}
                  >
                    {targetFrontendAddon?.name ?? ""}
                  </span>
                  ”のバージョン使用設定
                </>
              }
              rightHeaderElement={
                <div className={styles.headerActions}>
                  <Button
                    appearance="primary"
                    disabled={
                      isDisableTargetVersion ||
                      !isTargetVersionChanged ||
                      !targetVersion
                    }
                    onClick={() => setTargetVersionDialogPhase("confirm")}
                  >
                    保存
                  </Button>
                  <Button
                    appearance="secondary"
                    disabled={!isTargetVersionChanged || isInProgress}
                    onClick={runCancelTargetVersion}
                  >
                    キャンセル
                  </Button>
                </div>
              }
              bodyElement={
                <div className={styles.targetVersionSection}>
                  <InfoTooltipLabel info="この設定は組織固有です">
                    使用方法を選択
                  </InfoTooltipLabel>
                  <RadioGroup
                    className={styles.targetVersionContent}
                    value={targetVersionMode}
                    onChange={(_, data) => {
                      setTargetVersionMode(data.value as TargetVersionMode);
                    }}
                    disabled={isDisableTargetVersion}
                  >
                    {renderTargetVersionRadioCard({
                      mode: "latest",
                      title: "「latest」を使用",
                      description:
                        "常に最新のバージョンを使用します。新しいバージョンが追加されると自動的に切り替わります。",
                      children:
                        targetVersionMode === "latest" ? (
                          <div className={styles.targetVersionLatestName}>
                            現在の最新：{latestVersionName}
                          </div>
                        ) : null,
                    })}
                    {renderTargetVersionRadioCard({
                      mode: "fixed",
                      title: "バージョンを固定して使用",
                      description:
                        "指定したバージョンに固定されます。新しいバージョンが追加されても自動では切り替わりません。",
                      children:
                        targetVersionMode === "fixed" ? (
                          <Dropdown
                            className={styles.targetVersionDropdown}
                            selectedOptions={
                              fixedVersionId ? [fixedVersionId] : []
                            }
                            value={selectedFixedVersionName}
                            onOptionSelect={(_, data) => {
                              setFixedVersionId(data.optionValue ?? "");
                            }}
                            disabled={isDisableTargetVersion}
                          >
                            {sortedVersions.map((version) => (
                              <Option key={version.id} value={version.id}>
                                {formatFrontendAddonVersionDisplayName(version)}
                              </Option>
                            ))}
                          </Dropdown>
                        ) : null,
                    })}
                  </RadioGroup>
                </div>
              }
            />
          </div>
        ) : null}
        {/* GridTable のヘッダーとテーブルの間に cardsContainer の gap が入らないよう 1 要素に包む */}
        <div>
          <GridTableFrontendAddonVersions
            targetFrontendAddon={targetFrontendAddon}
            versions={sortedVersions}
            latestUsageOrgs={latestUsageOrgs}
            selectedVersionIds={selectedVersionIds}
            setSelectedVersionIds={setSelectedVersionIds}
            hasDeletePermission={hasDeletePermission}
            isAddonDisabled={targetFrontendAddon?.is_disabled ?? false}
            isAddonNotEditable={!(targetFrontendAddon?.is_editable ?? true)}
            onSingleAction={(versionId) => {
              setSelectedVersionIds([]);
              setSelectedVersionId(versionId);
              setSelectedAction("remove");
              setDialogPhase("confirm");
            }}
            onBulkAction={() => {
              setIsBulkDialogOpen(true);
            }}
            onVersionUpdate={() => navigate(versionUpdatePath)}
            hasWritePermission={hasWritePermission}
          />
        </div>
      </div>

      {renderTargetVersionDialogs({
        confirm: {
          element: isProd ? (
            <WarningMessageForProduction isEdit={true} />
          ) : (
            <>バージョン使用設定を更新しますか？</>
          ),
          title: "バージョン使用設定更新",
          buttonText: "更新",
          onClick: runSaveTargetVersion,
        },
        inProgress: {
          title: "バージョン使用設定の更新中",
        },
      })}

      <DialogFrontendAddonVersionSingleAction
        organizationId={organizationId}
        frontendAddonId={frontendAddonId}
        isAADeveloperOrAbove={isAADeveloperOrAbove}
        versions={versions}
        selectedVersionId={selectedVersionId}
        setSelectedVersionId={setSelectedVersionId}
        selectedAction={selectedAction}
        setSelectedAction={setSelectedAction}
        dialogPhase={dialogPhase}
        setDialogPhase={setDialogPhase}
        fetchFrontendAddonVersions={fetchFrontendAddonVersions}
        runReloadFrontendAddons={runReloadFrontendAddons}
      />

      <DialogFrontendAddonVersionBulkAction
        organizationId={organizationId}
        frontendAddonId={frontendAddonId}
        isAADeveloperOrAbove={isAADeveloperOrAbove}
        isOpen={isBulkDialogOpen}
        setIsOpen={setIsBulkDialogOpen}
        selectedBulkVersions={selectedBulkVersions}
        setSelectedVersionIds={setSelectedVersionIds}
        fetchFrontendAddonVersions={fetchFrontendAddonVersions}
        runReloadFrontendAddons={runReloadFrontendAddons}
      />
    </>
  );
};
